import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';

import Icon from '../../components/AppIcon';
import WorkoutSummaryCard from './components/WorkoutSummaryCard';
import FeedbackForm from './components/FeedbackForm';
import FeedbackAnalytics from './components/FeedbackAnalytics';
import FeedbackHistory from './components/FeedbackHistory';

const PostWorkoutFeedbackSystem = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading } = useAuth();
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [activeTab, setActiveTab] = useState('submit');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Mock recent workouts - in real app, fetch from workout_sessions table
  const recentWorkouts = [
    {
      id: 1,
      name: 'Upper Body Strength',
      date: '2026-01-24',
      duration: 65,
      exercises: 8,
      caloriesBurned: 420,
      completed: true,
      feedbackSubmitted: false,
      type: 'strength'
    },
    {
      id: 2,
      name: 'HIIT Cardio Blast',
      date: '2026-01-23',
      duration: 45,
      exercises: 6,
      caloriesBurned: 380,
      completed: true,
      feedbackSubmitted: true,
      type: 'cardio'
    },
    {
      id: 3,
      name: 'Leg Day Power',
      date: '2026-01-22',
      duration: 70,
      exercises: 10,
      caloriesBurned: 450,
      completed: true,
      feedbackSubmitted: true,
      type: 'strength'
    }
  ];

  const handleProvideFeedback = (workout) => {
    setSelectedWorkout(workout);
    setShowFeedbackForm(true);
  };

  const handleFeedbackSubmit = () => {
    setShowFeedbackForm(false);
    setSelectedWorkout(null);
    // Refresh workouts list
  };

  const tabs = [
    { id: 'submit', label: 'Submit Feedback', icon: 'MessageSquare' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'history', label: 'History', icon: 'History' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Post-Workout Feedback</h1>
          <p className="text-muted-foreground">Share your experience and help us improve your fitness journey</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-border">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'submit' && (
          <div className="space-y-6">
            {/* Pending Feedback Notice */}
            {recentWorkouts?.filter(w => !w?.feedbackSubmitted)?.length > 0 && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Feedback Pending</h3>
                    <p className="text-sm text-muted-foreground">
                      You have {recentWorkouts?.filter(w => !w?.feedbackSubmitted)?.length} workout(s) waiting for your feedback. Your input helps us personalize your experience!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Workouts */}
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-4">Recent Workouts</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recentWorkouts?.map(workout => (
                  <WorkoutSummaryCard
                    key={workout?.id}
                    workout={workout}
                    onProvideFeedback={handleProvideFeedback}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <FeedbackAnalytics />
        )}

        {activeTab === 'history' && (
          <FeedbackHistory />
        )}
      </main>

      <MobileBottomNav />

      {showFeedbackForm && selectedWorkout && (
        <FeedbackForm
          workout={selectedWorkout}
          onClose={() => {
            setShowFeedbackForm(false);
            setSelectedWorkout(null);
          }}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
};

export default PostWorkoutFeedbackSystem;